/**
 * Accordion Component
 * -------------------
 * Description:
 * This module implements an accessible, reusable accordion component for FAQs
 * or collapsible content sections. It supports mouse and keyboard interactions,
 * custom events (`accordion:open` / `accordion:close`), and basic ARIA roles for
 * screen readers. Accordions are auto-initialized on DOM ready, and multiple
 * accordion containers on a page are supported.
 *
 * IMPORTANT: Implicit Public Contracts
 * -----------------------------------
 * Even if this file is only referenced from templates (e.g., EJS), the following
 * behaviors are relied upon by external consumers and are therefore treated as
 * part of the public contract:
 * - Auto-initialization on DOM ready (initializeAccordions runs automatically)
 * - Class names and DOM structure: `.accordion` with `.accordion-header` + `.accordion-content`
 * - Safe re-initialization: `initializeAccordions()` can be called multiple times without
 *   double-binding listeners (guarded by `container._accordionInstance`).
 *
 * Changing any of the above is a breaking change and should be coordinated with
 * template/CSS updates.
 */

(function() {
  'use strict';

  // Configuration constants
  const DEFAULTS = {
    allowMultiple: false,
    animationDuration: 300,
    debug: false, // gates default open/close console logs
    cssClasses: {
      container: 'accordion',
      header: 'accordion-header',
      content: 'accordion-content',
      active: 'active'
    },
    ariaRoles: {
      button: 'button',
      region: 'region'
    }
  };

  // Store handlers without attaching properties to DOM nodes
  const HEADER_HANDLERS = new WeakMap(); // headerEl -> { clickHandler, keyHandler }

  // Instance counter to ensure unique instance IDs across the page
  let ACCORDION_INSTANCE_COUNTER = 0;

  // ID Generator utility (includes instanceId so ids never collide across accordions)
  class IDGenerator {
    constructor(prefix = 'acc', instanceId = '') {
      this.prefix = prefix;
      this.instanceId = instanceId;
      this.counter = 0;
    }

    generate(type) {
      this.counter++;
      return `${this.prefix}-${this.instanceId}-${type}-${this.counter}`;
    }

    reset() {
      this.counter = 0;
    }

    getCurrentCount() {
      return this.counter;
    }
  }

  // Accordion validation utility
  class AccordionValidator {
    static validate(element, options = {}) {
      const errors = [];

      if (!element) {
        errors.push('Container element is required');
        return { isValid: false, errors };
      }

      // NOTE: These default class names are part of an implicit contract with templates + CSS.
      const cssClasses = { ...DEFAULTS.cssClasses, ...options.cssClasses };
      const headers = element.querySelectorAll(`.${cssClasses.header}`);
      const contents = element.querySelectorAll(`.${cssClasses.content}`);

      if (headers.length === 0) {
        errors.push(`No accordion headers found with class '${cssClasses.header}'`);
      }

      if (contents.length === 0) {
        errors.push(`No accordion content found with class '${cssClasses.content}'`);
      }

      if (headers.length !== contents.length) {
        errors.push(`Header count (${headers.length}) doesn't match content count (${contents.length})`);
      }

      return {
        isValid: errors.length === 0,
        errors,
        headers: Array.from(headers),
        contents: Array.from(contents)
      };
    }
  }

  // Event handler factory
  class AccordionEventHandlers {
    constructor(accordion) {
      this.accordion = accordion;
    }

    createClickHandler(index) {
      return (e) => {
        e.preventDefault();
        this.accordion.toggle(index);
      };
    }

    createKeyHandler(index) {
      return (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.accordion.toggle(index);
        }
      };
    }
  }

  // Main Accordion class
  class Accordion {
    constructor(selector, options = {}) {
      this.options = this.mergeOptions(options);

      // Unique instance id across the page
      this.instanceId = `accordion-instance-${++ACCORDION_INSTANCE_COUNTER}`;

      // ID generator uses instanceId to prevent cross-accordion collisions
      this.idGenerator = new IDGenerator('accordion', this.instanceId);

      this.eventHandlers = new AccordionEventHandlers(this);

      // Initialize DOM references and validate
      this.initializeDOM(selector);

      if (this.isValid) {
        this.setupComponent();
      }
    }

    mergeOptions(userOptions) {
      return {
        ...DEFAULTS,
        ...userOptions,
        // NOTE: Changing these class names is a breaking change if templates/CSS rely on defaults.
        cssClasses: { ...DEFAULTS.cssClasses, ...userOptions.cssClasses },
        ariaRoles: { ...DEFAULTS.ariaRoles, ...userOptions.ariaRoles }
      };
    }

    initializeDOM(selector) {
      this.container = typeof selector === 'string'
        ? document.querySelector(selector)
        : selector;

      const validation = AccordionValidator.validate(this.container, this.options);

      this.isValid = validation.isValid;
      this.errors = validation.errors;
      this.headers = validation.headers || [];
      this.contents = validation.contents || [];

      if (!this.isValid) {
        console.error('Accordion initialization failed:', this.errors);
      }
    }

    setupComponent() {
      this.setupAccessibility();
      this.bindEvents();
      this.container.setAttribute('data-accordion-instance', this.instanceId);
    }

    bindEvents() {
      this.headers.forEach((header, index) => {
        const clickHandler = this.eventHandlers.createClickHandler(index);
        const keyHandler = this.eventHandlers.createKeyHandler(index);

        header.addEventListener('click', clickHandler);
        header.addEventListener('keydown', keyHandler);

        // Store handlers for cleanup (no DOM mutation)
        HEADER_HANDLERS.set(header, { clickHandler, keyHandler });
      });
    }

    setupAccessibility() {
      this.headers.forEach((header, index) => {
        const content = this.contents[index];

        const headerId = this.idGenerator.generate('header');
        const contentId = this.idGenerator.generate('content');

        this.setupHeaderAccessibility(header, headerId, contentId);

        if (content) {
          this.setupContentAccessibility(content, contentId, headerId);
        }
      });
    }

    setupHeaderAccessibility(header, headerId, contentId) {
      header.setAttribute('id', headerId);
      header.setAttribute('aria-controls', contentId);
      header.setAttribute('aria-expanded', 'false');
      header.setAttribute('role', this.options.ariaRoles.button);
      header.setAttribute('tabindex', '0');
    }

    setupContentAccessibility(content, contentId, headerId) {
      content.setAttribute('id', contentId);
      content.setAttribute('aria-labelledby', headerId);
      content.setAttribute('role', this.options.ariaRoles.region);
      content.setAttribute('aria-hidden', 'true');
      content.setAttribute('inert', '');
    }

    toggle(index) {
      if (!this.isValidIndex(index)) return;

      const isActive = this.isActive(index);

      if (!this.options.allowMultiple && !isActive) {
        this.closeAll();
      }

      isActive ? this.close(index) : this.open(index);
    }

    open(index) {
      if (!this.isValidIndex(index)) return;

      const header = this.headers[index];
      const content = this.contents[index];

      header.classList.add(this.options.cssClasses.active);
      header.setAttribute('aria-expanded', 'true');
      content.classList.add(this.options.cssClasses.active);
      content.setAttribute('aria-hidden', 'false');
      content.removeAttribute('inert');

      this.dispatchEvent('accordion:open', { index, header, content });
    }

    close(index) {
      if (!this.isValidIndex(index)) return;

      const header = this.headers[index];
      const content = this.contents[index];

      header.classList.remove(this.options.cssClasses.active);
      header.setAttribute('aria-expanded', 'false');
      content.classList.remove(this.options.cssClasses.active);
      content.setAttribute('aria-hidden', 'true');
      content.setAttribute('inert', '');

      this.dispatchEvent('accordion:close', { index, header, content });
    }

    closeAll() {
      this.headers.forEach((_, index) => {
        if (this.isActive(index)) {
          this.close(index);
        }
      });
    }

    openAll() {
      if (!this.options.allowMultiple) {
        console.warn('Cannot open all panels when allowMultiple is false');
        return;
      }

      this.headers.forEach((_, index) => {
        if (!this.isActive(index)) {
          this.open(index);
        }
      });
    }

    // Utility methods
    isValidIndex(index) {
      const valid = index >= 0 && index < this.headers.length;
      if (!valid) {
        console.warn(`Invalid accordion index: ${index}`);
      }
      return valid;
    }

    isActive(index) {
      return this.headers[index]?.classList.contains(this.options.cssClasses.active) || false;
    }

    dispatchEvent(eventType, detail) {
      this.container.dispatchEvent(new CustomEvent(eventType, { detail }));
    }

    // Public API methods
    getActiveIndexes() {
      return this.headers
        .map((header, index) => this.isActive(index) ? index : null)
        .filter(index => index !== null);
    }

    getInstanceInfo() {
      return {
        instanceId: this.instanceId,
        isValid: this.isValid,
        errors: this.errors,
        headerCount: this.headers.length,
        contentCount: this.contents.length,
        activeIndexes: this.getActiveIndexes()
      };
    }

    // Cleanup
    destroy() {
            this.headers.forEach(header => {
                const handlers = HEADER_HANDLERS.get(header);
                if (handlers) {
                    header.removeEventListener('click', handlers.clickHandler);
                    header.removeEventListener('keydown', handlers.keyHandler);
                    HEADER_HANDLERS.delete(header);
                    return;
                }

                // Backward-compatible fallback (in case older code set this)
                const legacyHandlers = header._accordionHandlers;
                if (legacyHandlers) {
                    header.removeEventListener('click', legacyHandlers.clickHandler);
                    header.removeEventListener('keydown', legacyHandlers.keyHandler);
                    delete header._accordionHandlers;
                }
            });

            // Remove data attributes
            this.container.removeAttribute('data-accordion-instance');

            // Preserve implicit public contract
            delete this.container._accordionInstance;
        }
    }

  // Factory function for easier instantiation
  function createAccordion(selector, options = {}) {
    return new Accordion(selector, options);
  }

  // Auto-initialization with better error handling
  function initializeAccordions(containerSelector = '.accordion', options = {}) {
    const containers = document.querySelectorAll(containerSelector);
    const instances = [];

    containers.forEach((container) => {
      // IMPORTANT: `_accordionInstance` is an implicit public/compatibility contract.
      // Do not remove without coordinating with templates/CSS and init logic.
      if (container._accordionInstance) {
        if (options && options.debug) {
          console.warn('Accordion already initialized on element:', container);
        }
        return;
      }

      const accordion = createAccordion(container, options);

      if (accordion.isValid) {
        container._accordionInstance = accordion;
        instances.push(accordion);

        // Optional: Add default event listeners (debug-only)
        const debug = !!(options && options.debug);
        if (debug) {
          container.addEventListener('accordion:open', (e) => {
            console.log(`Panel ${e.detail.index} opened in accordion ${accordion.instanceId}`);
          });

          container.addEventListener('accordion:close', (e) => {
            console.log(`Panel ${e.detail.index} closed in accordion ${accordion.instanceId}`);
          });
        }
      }
    });

    return instances;
  }

  // Convenience alias for dynamic pages: safe to call repeatedly
  function refreshAccordions(containerSelector = '.accordion', options = {}) {
    return initializeAccordions(containerSelector, options);
  }

  // DOM ready utility
  function domReady(callback) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback);
    } else {
      callback();
    }
  }

  // Auto-initialize on DOM ready
  domReady(() => {
    initializeAccordions();
  });

  // Expose public API
  if (typeof window !== 'undefined') {
    window.Accordion = Accordion;
    window.createAccordion = createAccordion;
    window.initializeAccordions = initializeAccordions;
    window.refreshAccordions = refreshAccordions;

    // Utilities for debugging/advanced usage
    window.AccordionValidator = AccordionValidator;
    window.AccordionIDGenerator = IDGenerator;
  }

})();
